import { userApi, useUpdateUserInfoMutation } from '@/store/api/userApi';
import { handleApiError, showToastMessage } from '@/utils/helper';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useOtpTimer } from './useOtpTimer';
import { useSendOtpMutation, useVerifyMobileMutation } from '@/store/api/authApi';

const useProfileHook = () => {
  const [isEditing, setEditng] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const { timeLeft, active, start, reset: resetTimer } = useOtpTimer(120);
  const [updateUserInfo, { isLoading: updateUserLoading }] = useUpdateUserInfoMutation();
  const [sendOtp, { isLoading: otpLoading }] = useSendOtpMutation();
  const [verifyMobile, { isLoading: isVerifyLoading }] = useVerifyMobileMutation();
  const profileQuery = useSelector(userApi.endpoints.getUserProfile.select());
  const userData = profileQuery?.data;
  const [phone, setPhone] = useState<{
    country?: string;
    countryCode: string;
    fullNumber: string;
    phoneNumber: string;
    dialCode: string;
  }>({
    country: '',
    countryCode: '',
    fullNumber: '',
    phoneNumber: '',
    dialCode: '',
  });
  const { control, handleSubmit, reset, resetField } = useForm({
    defaultValues: {
      username: '',
      mobileNumber: '',
      credits: 0,
      phone: '',
      referralCount: '',
      otp: '',
    },
  });
  useEffect(() => {
    if (userData) {
      reset({
        username: userData.username || '',
        mobileNumber: userData.mobile || '',
        phone: `${userData.mobile}`,
        referralCount: userData.referralCount || '',
        otp: '',
        credits: userData.credits || 0,
      });

      setPhone({
        country: userData.country,
        countryCode: userData.dialCode,
        dialCode: userData.dialCode,
        phoneNumber: userData.mobile?.replace(userData.dialCode ?? '', '') || '',
        fullNumber: userData.mobile || '',
      });
    }
  }, [userData, reset]);

  const onSubmit = async (data: any) => {
    try {
      if (!userData?.id) {
        showToastMessage('error', 'User information not found');
        return;
      }

      const mobileChanged =
        phone.phoneNumber !== userData?.mobile || phone.dialCode !== userData?.dialCode;

      if (mobileChanged) {
        if (!showOtpField) {
          showToastMessage('warning', 'Please verify your new mobile number first.');
          return;
        }

        if (!data.otp) {
          showToastMessage('warning', 'Please enter the OTP sent to your mobile.');
          return;
        }

        await verifyMobile({
          code: data.otp,
          mobile: phone.fullNumber,
        }).unwrap();
      }

      const payload = {
        id: userData.id,
        username: data.username,
        dialCode: phone.countryCode,
        mobile: phone.fullNumber,
        country: phone.country,
        mobileVerificationCode: data.otp,
      };

      const response = await updateUserInfo(payload).unwrap();

      showToastMessage('success', response?.message || 'Profile updated successfully');

      setEditng(false);
      setShowOtpField(false);
      resetField('otp');
    } catch (error: any) {
      showToastMessage('error', error?.data?.message || 'Failed to update profile');
    }
  };
  const handleSendOtp = async () => {
    if (!phone.phoneNumber?.trim()) {
      showToastMessage('error', 'Please enter a valid phone number.');
      return;
    }

    const payload = {
      mobile: phone.fullNumber,
      country: phone.country,
      type: 'verify-mobile',
    };

    try {
      await sendOtp(payload).unwrap();
      showToastMessage('success', 'OTP sent successfully!');
      setShowOtpField(true);
      start();
    } catch (err: any) {
      handleApiError(err || 'Failed to send OTP');
    }
  };
  const handleResetOtpField = () => {
    setShowOtpField(false);
    resetTimer();
    resetField('otp');
  };

  return {
    control,
    isEditing,
    setEditng,
    updateUserLoading,
    onSubmit,
    phone,
    setPhone,
    showOtpField,
    setShowOtpField,
    timeLeft,
    handleSendOtp,
    handleResetOtpField,
    resetField,
    active,
    handleSubmit,
  };
};

export default useProfileHook;
